import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { authApi, type LoginPayload, type RegisterPayload } from '@/features/auth/api';
import { organizationApi } from '@/features/organization/api';
import { clearTokens, getAccessToken, getRefreshToken, saveTokens } from '@/services/tokenStorage';
import type { MeResult, OrganizationSummary, PublicUser } from '@/types/auth';
import { ApiRequestError } from '@/services/httpClient';
import { setOrgDefaultCurrency } from '@/utils/format';

interface AuthContextValue {
  user: PublicUser | null;
  organizationId: number | null;
  roles: string[];
  permissions: string[];
  organizations: OrganizationSummary[];
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (payload: LoginPayload) => Promise<LoginOutcome>;
  loginWithGoogle: (idToken: string, organizationName?: string, organizationId?: number) => Promise<LoginOutcome>;
  register: (payload: RegisterPayload) => Promise<void>;
  logout: (everywhere?: boolean) => Promise<void>;
  refreshProfile: () => Promise<void>;
  hasPermission: (slug: string) => boolean;
  switchOrganization: (organizationId: number) => Promise<void>;
}

export type LoginOutcome =
  | { status: 'success' }
  | { status: 'requires_organization_selection'; organizations: OrganizationSummary[] }
  | { status: 'requires_organization_name'; email: string; firstName: string; lastName: string | null };

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<MeResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadProfile = useCallback(async () => {
    if (!getAccessToken()) {
      setProfile(null);
      setIsLoading(false);
      return;
    }
    try {
      const me = await authApi.me();
      setProfile(me);
      // Fire-and-forget — doesn't gate isLoading, just applies the org's
      // currency default (see utils/format.ts) once it resolves.
      void organizationApi.get().then((org) => setOrgDefaultCurrency(org.currency)).catch(() => undefined);
    } catch {
      clearTokens();
      setProfile(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  const login = useCallback(async (payload: LoginPayload): Promise<LoginOutcome> => {
    const result = await authApi.login(payload);

    if (result.requires_organization_selection) {
      return { status: 'requires_organization_selection', organizations: result.organizations ?? [] };
    }

    if (!result.access_token || !result.refresh_token || result.organization_id === undefined) {
      throw new ApiRequestError('Login response was missing token data.');
    }

    saveTokens({
      accessToken: result.access_token,
      refreshToken: result.refresh_token,
      organizationId: result.organization_id,
    });
    await loadProfile();
    return { status: 'success' };
  }, [loadProfile]);

  const loginWithGoogle = useCallback(async (idToken: string, organizationName?: string, organizationId?: number): Promise<LoginOutcome> => {
    const result = await authApi.loginWithGoogle(idToken, organizationName, organizationId);

    if (result.requires_organization_name) {
      return {
        status: 'requires_organization_name',
        email: result.email ?? '',
        firstName: result.first_name ?? '',
        lastName: result.last_name ?? null,
      };
    }
    if (result.requires_organization_selection) {
      return { status: 'requires_organization_selection', organizations: result.organizations ?? [] };
    }
    if (!result.access_token || !result.refresh_token || result.organization_id === undefined) {
      throw new ApiRequestError('Google sign-in response was missing token data.');
    }

    saveTokens({
      accessToken: result.access_token,
      refreshToken: result.refresh_token,
      organizationId: result.organization_id,
    });
    await loadProfile();
    return { status: 'success' };
  }, [loadProfile]);

  const register = useCallback(async (payload: RegisterPayload) => {
    const result = await authApi.register(payload);
    if (!result.access_token || !result.refresh_token || result.organization_id === undefined) {
      throw new ApiRequestError('Registration response was missing token data.');
    }
    saveTokens({
      accessToken: result.access_token,
      refreshToken: result.refresh_token,
      organizationId: result.organization_id,
    });
    await loadProfile();
  }, [loadProfile]);

  const logout = useCallback(async (everywhere = false) => {
    try {
      await authApi.logout(getRefreshToken(), everywhere);
    } finally {
      clearTokens();
      setProfile(null);
    }
  }, []);

  const hasPermission = useCallback(
    (slug: string) => Boolean(profile?.user.is_super_admin) || (profile?.permissions.includes(slug) ?? false),
    [profile],
  );

  const switchOrganization = useCallback(async (organizationId: number) => {
    const currentRefreshToken = getRefreshToken();
    if (!currentRefreshToken) {
      throw new ApiRequestError('No active session to switch from.');
    }
    const result = await authApi.refresh(currentRefreshToken, organizationId);
    if (!result.access_token || !result.refresh_token || result.organization_id === undefined) {
      throw new ApiRequestError('Switch-organization response was missing token data.');
    }
    saveTokens({
      accessToken: result.access_token,
      refreshToken: result.refresh_token,
      organizationId: result.organization_id,
    });
    await loadProfile();
  }, [loadProfile]);

  const value = useMemo<AuthContextValue>(() => ({
    user: profile?.user ?? null,
    organizationId: profile?.organization_id ?? null,
    roles: profile?.roles ?? [],
    permissions: profile?.permissions ?? [],
    organizations: profile?.organizations ?? [],
    isAuthenticated: profile !== null,
    isLoading,
    login,
    loginWithGoogle,
    register,
    logout,
    refreshProfile: loadProfile,
    hasPermission,
    switchOrganization,
  }), [profile, isLoading, login, loginWithGoogle, register, logout, loadProfile, hasPermission, switchOrganization]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
